// ModalMapOrdersMB.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../common/Modal";
import "../../styles/map-modal.css";

const MAPBOX_SCRIPT_URL = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
//const MAPBOX_STYLESHEET_URL = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
const MAPBOX_STYLE = "mapbox://styles/mapbox/streets-v12";
const DEFAULT_CENTER = [-3.7038, 40.4168];
const DEFAULT_ZOOM = 5;

let mapboxLoadPromise = null;

const loadMapboxFromCdn = () => {
  if (typeof window === "undefined") return Promise.reject();
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (mapboxLoadPromise) return mapboxLoadPromise;

  mapboxLoadPromise = new Promise((resolve, reject) => {
    const ensureStylesheet = () => {
  /*    if (!document.querySelector(`link[href="${MAPBOX_STYLESHEET_URL}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = MAPBOX_STYLESHEET_URL;
        document.head.appendChild(link);
      }*/
    };
    ensureStylesheet();

    const script = document.createElement("script");
    script.src = MAPBOX_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = () => reject(new Error("No se pudo cargar Mapbox GL JS"));
    document.body.appendChild(script);
  });

  return mapboxLoadPromise;
};

const DEFAULT_STATE_COLORS = {
  "1": "#d9534f",
  "2": "#f0ad4e",
  "3": "#6c757d",
  "4": "#28a745",
};

const buildLegendControl = (legendItems) => {
  let container;

  return {
    onAdd() {
      container = document.createElement("div");
      container.className = "map-modal__legend mapboxgl-ctrl";

      const title = document.createElement("h4");
      title.textContent = "Estados";
      container.appendChild(title);

      const list = document.createElement("ul");
      legendItems.forEach(({ color, label, state }) => {
        const item = document.createElement("li");

        const colorDot = document.createElement("span");
        colorDot.className = "map-modal__legend-color";
        colorDot.style.backgroundColor = color;

        const text = document.createElement("span");
        text.textContent = label || `Estado ${state}`;

        item.appendChild(colorDot);
        item.appendChild(text);
        list.appendChild(item);
      });

      container.appendChild(list);
      return container;
    },
    onRemove() {
      if (container?.parentNode) {
        container.parentNode.removeChild(container);
      }
      container = null;
    },
  };
};

function ModalMapOrders({
  isOpen,
  onClose,
  orders,
  focusedOrderId,
  estadoLabels = {},
  tipoParteLabels = {},
  //resolveTechnicianName,
  //resolveGroupName,
  clientName = "",
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapboxRef = useRef(null);
  const [mapboxReady, setMapboxReady] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState("");

  const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "";

  // SOSTITUISCI il blocco ordersWithCoordinates (righe ~90-120) con:
const ordersWithCoordinates = useMemo(() => {
    if (!Array.isArray(orders)) return [];

    const parsedOrders = orders
      .map((order) => {
        const lat = Number(String(order?.COORDENADA_X || "").replace(",", "."));
        const lng = Number(String(order?.COORDENADA_Y || "").replace(",", "."));
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0))
          return null;
        return { order, lat, lng };
      })
      .filter(Boolean);

    if (parsedOrders.length === 0) return [];

    // Raggruppa per coordinate
    const groupedByCoordinate = parsedOrders.reduce((groups, item) => {
      const key = `${item.lat.toFixed(6)}|${item.lng.toFixed(6)}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
      return groups;
    }, new Map());

    // Shifta i duplicati verso destra
    const shiftedOrders = [];
    const horizontalOffset = 0.0001; // Distanza tra pallini (regolabile)

    groupedByCoordinate.forEach((group) => {
      group.forEach((item, index) => {
        if (index === 0) {
          // Primo pallino: posizione originale
          shiftedOrders.push(item);
        } else {
          // Pallini successivi: shift verso destra
          const lngOffset = horizontalOffset * index;
          shiftedOrders.push({
            ...item,
            lng: item.lng + lngOffset,
          });
        }
      });
    });

    return shiftedOrders;
  }, [orders]);

  const geojsonData = useMemo(() => ({
    type: "FeatureCollection",
    features: ordersWithCoordinates.map(({ order, lat, lng }) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [lng, lat] },
      properties: {
        id: String(order?.COD_ID || ""),
        estado: String(order?.Estado || ""),
        cups: order?.CUPS || "Sin CUPS",
        tipo: tipoParteLabels?.[order?.TipoParte] || order?.TipoParte || "",
        tecnico: order?.USER_NAME || "",
        grupo: order?.USER_GROUP_NAME || "",
        direccion: order?.DIRECCIONSUMINISTRO || "",
        labelEstado: estadoLabels?.[order?.Estado] || order?.Estado || "",
      },
    })),
  }), [ordersWithCoordinates, estadoLabels, tipoParteLabels]); // , resolveTechnicianName, resolveGroupName
  
  const legendItems = useMemo(
    () =>
      Object.entries(DEFAULT_STATE_COLORS).map(([state, color]) => ({
        state,
        color,
        label: estadoLabels?.[state],
      })),
    [estadoLabels]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!mapboxToken) {
      setMapError("Falta la variable VITE_MAPBOX_ACCESS_TOKEN");
      return;
    }

    let cancelled = false;
    setMapError("");
    setMapboxReady(false);

    loadMapboxFromCdn()
      .then((mapboxgl) => {
        if (cancelled) return;
        mapboxgl.accessToken = mapboxToken;
        mapboxRef.current = mapboxgl;
        setMapboxReady(true);
      })
      .catch((err) => {
        if (!cancelled) setMapError(err.message);
      });

    return () => {
      cancelled = true;
      setMapboxReady(false);
      setMapLoaded(false);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isOpen, mapboxToken]);

  useEffect(() => {
    if (!isOpen || !mapboxReady) return;
    const mapboxgl = mapboxRef.current;
    if (!mapboxgl || !mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    mapInstanceRef.current = map;

    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.addControl(new mapboxgl.FullscreenControl());
    let legendControl;

    map.on("load", () => {
      setMapLoaded(true);

      map.addSource("orders", {
        type: "geojson",
        data: geojsonData,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Cluster circles
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "orders",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#0d6efd",
          "circle-radius": [
            "step",
            ["get", "point_count"],
            18,
            100,
            24,
            750,
            30,
          ],
          "circle-opacity": 0.8,
        },
      });

      // Cluster count labels
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "orders",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["Open Sans Bold"],
          "text-size": 12,
        },
        paint: { "text-color": "#ffffff" },
      });

      // Individual points
      map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "orders",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match",
            ["get", "estado"],
            "1", DEFAULT_STATE_COLORS["1"],
            "2", DEFAULT_STATE_COLORS["2"],
            "3", DEFAULT_STATE_COLORS["3"],
            "4", DEFAULT_STATE_COLORS["4"],
            "#007bff",
          ],
          "circle-radius": 7,
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "#fff",
        },
      });

      // Zoom on cluster click
      map.on("click", "clusters", (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
        const clusterId = features[0].properties.cluster_id;
        map.getSource("orders").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom,
          });
        });
      });

      // Popup for single point
      map.on("click", "unclustered-point", (e) => {
        const feature = e.features[0];
        const props = feature.properties;

        const popupHtml = `
          <div class="map-modal__popup">
            <div class="map-modal__popup-item map-modal__popup-item--primary">${props.cups}</div>
            <div class="map-modal__popup-item"><span class="map-modal__popup-label">Tipo:</span> ${props.tipo}</div>
            <div class="map-modal__popup-item"><span class="map-modal__popup-label">Estado:</span> ${props.labelEstado}</div>
            <div class="map-modal__popup-item"><span class="map-modal__popup-label">Técnico:</span> ${props.tecnico}</div>
            <div class="map-modal__popup-item"><span class="map-modal__popup-label">Grupo:</span> ${props.grupo}</div>
            <div class="map-modal__popup-item"><span class="map-modal__popup-label">Dirección:</span> ${props.direccion}</div>
          </div>`;

        new mapboxgl.Popup({ closeButton: true, offset: 18 })
          .setLngLat(feature.geometry.coordinates)
          .setHTML(popupHtml)
          .addTo(map);
      });

      map.on("mouseenter", "unclustered-point", () => map.getCanvas().style.cursor = "pointer");
      map.on("mouseleave", "unclustered-point", () => map.getCanvas().style.cursor = "");
      let hoverPopup = null;

      map.on("mouseenter", "unclustered-point", (e) => {
        map.getCanvas().style.cursor = "pointer";
        const feature = e.features[0];
        const cups = feature?.properties?.cups || "";

        if (!cups) return;

        // Popup leggero solo per hover (senza click)
        hoverPopup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 10,
        })
          .setLngLat(feature.geometry.coordinates)
          .setHTML(`<div class="map-modal__hover">${cups}</div>`)
          .addTo(map);
      });

      map.on("mouseleave", "unclustered-point", () => {
        map.getCanvas().style.cursor = "";
        if (hoverPopup) {
          hoverPopup.remove();
          hoverPopup = null;
        }
      });

      // Fit to bounds
      if (ordersWithCoordinates.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        ordersWithCoordinates.forEach(({ lng, lat }) => bounds.extend([lng, lat]));
        map.fitBounds(bounds, { padding: 70, maxZoom: 13, duration: 1000 });
      }
      legendControl = buildLegendControl(legendItems);
      map.addControl(legendControl, "bottom-left");

      map.once("remove", () => {
        try {
          map.removeControl(legendControl);
        } catch (err) {
          console.warn("Legend control cleanup skipped:", err);
        }
      });
    });

    return () => {
      if (map && map.remove) {
        try {
          if (legendControl) {
            map.removeControl(legendControl);
          }
          map.remove();
        } catch (err) {
          console.warn("Map cleanup skipped:", err);
        }
      }
    };
  }, [isOpen, mapboxReady, geojsonData]);

  return (
    <Modal
      title={clientName ? `Partes de ${clientName} en mapa` : "Partes visibles en mapa"}
      onClose={onClose}
      className={`modal-map`}
    >
      <div className={`map-modal__body`}>
        {mapError && (
          <div className="map-modal__empty">
            <p>{mapError}</p>
          </div>
        )}

        {!mapError && (
          <>
            <div className="map-modal__map-wrapper">
              <div ref={mapContainerRef} className="map-modal__map" />
              {!mapLoaded && <div className="map-modal__loading">Cargando mapa…</div>}
          </div>
          </>
        )}
      </div>


    </Modal>
  );
}

export default ModalMapOrders;
